import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import CenteredLayout from '../../layout/CenteredLayout';
import { AppColors } from '../../styles/colors';
import { AppTextStyles } from '../../styles/textStyles';

/**
 * NotFound는 존재하지 않는 페이지(404) 요청 시 보여지는 커스텀 에러 페이지입니다.
 * React Router에서 catch-all 라우트로 설정하여 사용합니다.
 *
 * NotFound is the custom 404 page shown when users access a non-existent route.
 * Used as a catch-all route in React Router configuration.
 *
 * [디자인 수정 안내 / How to Modify the Design]
 * - 이 컴포넌트의 레이아웃, 텍스트, 색상, 버튼 등을 수정하여 프로젝트의 디자인 시스템에 맞게 커스터마이징 하세요.
 * - 예: 헤더 이미지 추가, 버튼 스타일 변경, 다크모드 대응 등
 */

const Container = styled.main`
  padding: 4rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: ${AppColors.background};
`;

const Title = styled.h1`
  font-size: ${AppTextStyles.headline1.fontSize};
  font-weight: ${AppTextStyles.headline1.fontWeight};
  color: ${AppColors.onBackground};
  margin-bottom: 1rem;
`;

const Description = styled.p`
  font-size: ${AppTextStyles.body1.fontSize};
  color: ${AppColors.onInput2};
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    gap: 1.5rem;
  }
`;

const StyledButton = styled.button<{ $bgColor: string }>`
  padding: 12px 24px;
  background-color: ${({ $bgColor }) => $bgColor};
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: bold;
  font-size: 14px;
  cursor: pointer;
  min-width: 160px;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.9;
  }
  
  &:active {
    opacity: 0.8;
  }
`;

const NotFound: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <CenteredLayout>
      <Container>
        <Title>🚫 페이지를 찾을 수 없습니다</Title>
        <Description>
          요청하신 주소에 해당하는 페이지가 존재하지 않거나 삭제되었습니다.
        </Description>

        <ButtonContainer>
          <StyledButton 
            $bgColor={AppColors.primary}
            onClick={() => handleNavigate('/')}
          >
            홈으로 돌아가기
          </StyledButton>
          
        </ButtonContainer>
      </Container>
    </CenteredLayout>
  );
};

export default NotFound;
